import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { TableCell } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import { type Nil, PermissionArea, useFields } from '@pbt/pbt-ui-components'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import UserSelect from '~/components/common/inputs/UserSelect'
import { getIsRetailOrderLineItem } from '~/components/dashboard/invoices/invoiceUtils'
import { FINANCE_CELL_WIDTH } from '~/constants/financeTable'
import {
  editChargeSheetProducerItemBatch,
  editChargeSheetRetailOrderLineItemProducerBatch,
} from '~/store/duck/clientFinanceData'
import { getCRUDByArea } from '~/store/reducers/auth'
import {
  updateInvoiceLineItemProducerBatch,
  updateRetailOrderLineItemProducerBatch,
} from '~/store/reducers/invoiceV3'
import type { InvoiceLineItem } from '~/types'

const { PRODUCER } = FINANCE_CELL_WIDTH

const useStyles = makeStyles(
  (theme) => ({
    producerCell: {
      minWidth: PRODUCER,
      width: PRODUCER,
    },
    fullyRefunded: {
      color: theme.colors.lowAccentText,
    },
  }),
  { name: 'FinanceItemProducerCellBody' },
)

export interface FinanceItemProducerCellBodyProps {
  allowModifyForInvoiceDoctorOnly: boolean
  invoiceId?: string
  isFullyRefunded?: boolean
  item: InvoiceLineItem | RetailOrderLineItem
  tableCellClassName: string
  tableCellWithBorder: string
}

const FinanceItemProducerCellBody = ({
  item,
  isFullyRefunded = false,
  tableCellClassName,
  tableCellWithBorder,
  allowModifyForInvoiceDoctorOnly,
  invoiceId,
}: FinanceItemProducerCellBodyProps) => {
  const classes = useStyles()

  const dispatch = useDispatch()
  const invoiceDoctorPermission = useSelector(
    getCRUDByArea(PermissionArea.INVOICE_DOCTOR),
  )

  const isRetailOrderLineItem = getIsRetailOrderLineItem(item)

  const dispatchChargeSheetProducerItemCharges = (value: string | Nil) =>
    dispatch(
      editChargeSheetProducerItemBatch({
        clientId: (item as InvoiceLineItem).clientId, // Won't ever be RetailOrderLineItem
        updateItemProducerInput: [
          {
            producerId: value as string,
            id: item.id,
            expectedModification: item.modificationDate,
          },
        ],
      }),
    )

  const dispatchInvoiceProducerItemChanges = (value: string | Nil) =>
    dispatch(
      updateInvoiceLineItemProducerBatch([
        {
          id: item.id,
          expectedModification: item.modificationDate,
          producerId: value as string,
        },
      ]),
    )

  const setValueFun = isRetailOrderLineItem
    ? (value: string | Nil) =>
        invoiceId
          ? dispatch(
              updateRetailOrderLineItemProducerBatch([
                {
                  id: item.id,
                  producerId: value || '',
                  expectedModification: item.modificationDate,
                },
              ]),
            )
          : dispatch(
              editChargeSheetRetailOrderLineItemProducerBatch([
                {
                  id: item.id,
                  producerId: value || '',
                  expectedModification: item.modificationDate,
                },
              ]),
            )
    : (value: string | Nil) =>
        invoiceId
          ? dispatchInvoiceProducerItemChanges(value)
          : dispatchChargeSheetProducerItemCharges(value)

  const { producerId } = item || ''

  const editable =
    !isFullyRefunded &&
    (!allowModifyForInvoiceDoctorOnly || invoiceDoctorPermission.update)

  const { fields, updateState } = useFields(
    [
      {
        name: 'producer',
        initialValue: producerId,
        type: 'select',
      },
    ],
    false,
  )

  useEffect(() => {
    if (producerId) {
      updateState({ producer: producerId })
    }
  }, [producerId])

  return (
    <TableCell
      className={classNames(
        tableCellClassName,
        tableCellWithBorder,
        classes.producerCell,
        {
          [classes.fullyRefunded]: isFullyRefunded,
        },
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {!item.declined && (
        <UserSelect
          plain
          disabled={!editable}
          field={{
            ...fields.producer,
            setValue: setValueFun,
          }}
        />
      )}
    </TableCell>
  )
}
export default FinanceItemProducerCellBody
